import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { X, Maximize2 } from "lucide-react";
import { ARTWORKS } from "../data/content";
import { usePhase } from "../context/PhaseContext";
import { Reveal } from "./Reveal";

const GALLERY_LAYOUT = ["md:col-span-7", "md:col-span-5 md:mt-40", "md:col-span-7 md:mt-24"];
const GALLERY_PARALLAX = [30, 50, 40, 20];

const Frame = ({ art, parallax = 0, onOpen }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [parallax, -parallax]);

  return (
    <motion.figure ref={ref} style={{ y }} data-testid={`artwork-${art.id}`} className="group relative">
      <button
        onClick={() => onOpen(art)}
        data-cursor="art"
        data-testid={`artwork-open-${art.id}`}
        className="relative block w-full bg-bds-cream p-4 sm:p-6 md:p-8 overflow-hidden shadow-[0_30px_90px_-30px_rgba(0,0,0,0.85)] text-left"
      >
        <div className="overflow-hidden">
          <img
            src={art.img}
            alt={art.subtitle}
            draggable={false}
            className="w-full h-auto select-none transition-transform duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.06]"
          />
        </div>
        <span className="absolute top-4 left-4 font-mono-bds text-[11px] tracking-[0.2em] text-black/50">
          BDS — {art.index}
        </span>
        <span className="absolute bottom-4 right-4 w-10 h-10 grid place-items-center bg-bds-bg/80 text-bds-cream opacity-0 group-hover:opacity-100 transition-opacity">
          <Maximize2 size={15} />
        </span>
      </button>
      <figcaption className="mt-6 flex items-start justify-between gap-4">
        <div>
          <h3 className="font-serif-display text-3xl md:text-4xl tracking-tight">{art.title}</h3>
          <p className="font-mono-bds text-[11px] uppercase tracking-[0.25em] text-bds-muted mt-2">
            {art.subtitle}
          </p>
        </div>
        <p className="hidden sm:block font-grotesk text-sm text-bds-muted max-w-[15rem] text-right leading-relaxed">
          {art.note}
        </p>
      </figcaption>
    </motion.figure>
  );
};

const Lightbox = ({ art, onClose, commerce }) => (
  <motion.div
    className="fixed inset-0 z-[70] flex items-center justify-center p-4 md:p-10"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    data-testid="artwork-lightbox"
  >
    <div className="absolute inset-0 bg-bds-bg/90 backdrop-blur-md" onClick={onClose} />
    <button
      onClick={onClose}
      data-testid="lightbox-close"
      className="absolute top-6 right-6 z-10 w-12 h-12 grid place-items-center border border-bds-border text-bds-cream hover:bg-bds-fg hover:text-bds-bg transition-colors"
    >
      <X size={18} />
    </button>
    <motion.div
      initial={{ scale: 0.94, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      exit={{ scale: 0.94, opacity: 0 }}
      transition={{ ease: [0.22, 1, 0.36, 1], duration: 0.5 }}
      className="relative grid grid-cols-1 md:grid-cols-[1.4fr_1fr] gap-8 max-w-5xl w-full items-center"
    >
      <div className="bg-bds-cream p-6 md:p-12 max-h-[80vh] overflow-hidden flex items-center justify-center">
        <img src={art.img} alt={art.subtitle} className="max-h-[70vh] w-auto" />
      </div>
      <div>
        <span className="font-mono-bds text-xs tracking-[0.3em] text-bds-accent">{art.index} / {String(ARTWORKS.length).padStart(2, "0")}</span>
        <h3 className="font-serif-display text-5xl md:text-6xl tracking-tight mt-4 mb-3">{art.title}</h3>
        <p className="font-mono-bds text-[11px] uppercase tracking-[0.25em] text-bds-muted mb-6">{art.subtitle}</p>
        <p className="font-grotesk text-bds-muted leading-relaxed mb-8">{art.note}</p>
        {commerce ? (
          <a
            href="#shop"
            onClick={onClose}
            data-testid="lightbox-shop-btn"
            className="inline-block font-mono-bds text-xs uppercase tracking-[0.2em] px-8 py-4 bg-bds-fg text-bds-bg hover:bg-bds-accent hover:text-bds-cream transition-colors"
          >
            Wear this design
          </a>
        ) : (
          <Link
            to="/archive"
            data-testid="lightbox-archive-btn"
            className="inline-block font-mono-bds text-xs uppercase tracking-[0.2em] px-8 py-4 border border-bds-border text-bds-cream hover:border-bds-fg transition-colors"
          >
            View the Archive
          </Link>
        )}
      </div>
    </motion.div>
  </motion.div>
);

export const Gallery = () => {
  const [active, setActive] = useState(null);
  const { phase } = usePhase();
  return (
    <section id="gallery" data-testid="gallery" className="relative py-28 md:py-44">
      <div className="mx-auto max-w-[1600px] px-6 md:px-12">
        <Reveal>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16 md:mb-28">
            <div>
              <span className="font-mono-bds text-xs uppercase tracking-[0.3em] text-bds-accent">
                Original Works
              </span>
              <h2 className="font-serif-display font-light text-5xl md:text-7xl lg:text-8xl tracking-tighter leading-[0.9] mt-6">
                Ink from <br /> the inside
              </h2>
            </div>
            <p className="font-grotesk text-bds-muted max-w-sm leading-relaxed">
              Three original hand-drawn pieces. Each one a feeling made visible — tap any piece to look closer.
            </p>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-x-10 md:gap-y-28">
          {ARTWORKS.map((art, i) => (
            <div key={art.id} className={GALLERY_LAYOUT[i % GALLERY_LAYOUT.length]}>
              <Frame art={art} parallax={GALLERY_PARALLAX[i % GALLERY_PARALLAX.length]} onOpen={setActive} />
            </div>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {active && <Lightbox art={active} onClose={() => setActive(null)} commerce={phase >= 5} />}
      </AnimatePresence>
    </section>
  );
};
